/**
 * The security card of a Saidiseire site page (item B,
 * docs/saidiseire-kiirus-turve-raport.md): the weekly scan's verdict, what is
 * new since the last scan, the vulnerabilities matched against the Wordfence
 * feed with their licence texts, the other findings and what the scan could
 * not cover.
 *
 * The states, in the order they are decided (`securityCardState`):
 *  - not in the package: say so (a site with no package at all is told that
 *    the check needs one, not that "its package" leaves it out); a scan
 *    stored before the package dropped it stays visible under a line that
 *    says it is the last one;
 *  - no Wordfence key: nothing can run; link to Settings > Integrations >
 *    Wordfence for someone who may open it, a line for anyone else;
 *  - a key, but the feed was never downloaded: why (the download's own error
 *    when it keeps failing, a refused key above all, rather than a promise of
 *    a first check that never comes), and the same link, or for someone who
 *    may not open it a line saying who can fix it;
 *  - never scanned: when the first weekly scan is due;
 *  - scanned: the verdict and the details.
 * On top of any of them: a scan going on (polled every 5 s), one that takes
 * longer than a scan may (polling stopped, a refresh button), and the last
 * run's error.
 *
 * "Check now" answers to `manage:crm.monitoring`; the server refuses what it
 * must (`periodic/manual.ts`) and the refusal is worded by `monitorErrorKey`.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, RefreshCw, ShieldCheck } from "lucide-react";
import { monitorErrorKey } from "../../../../lib/monitoring";
import { feedErrorKey } from "../../../../lib/monitoringKeys";
import { WORDFENCE_SETTINGS_PATH, canScanNow, securityCardState, verdictTone } from "../../../../lib/monitoringSecurity";
import { Btn, CardHead, StatusPill, fmtDateTime, useToast } from "../../../ui";
import type { T } from "../words";
import { Attributions } from "./Attributions";
import { ScanDetails } from "./ScanDetails";
import { useSecurity } from "./useSecurity";

export interface SecurityCardProps {
  monitorId: string;
  /** The site's package includes the security scan (`MonitorPolicyView.securityScan`). */
  allowed: boolean;
  /** The site has a package at all (`MonitorPolicyView.source === "plan"`). */
  hasPackage: boolean;
  /** `manage:crm.monitoring`: may start a scan on request, and may open the Wordfence settings. */
  canManage: boolean;
  t: T;
}

/** The Wordfence settings, for someone who may open them (the tab needs `manage:crm.monitoring`); nothing for anyone else. */
function SettingsLink({ canManage, t }: { canManage: boolean; t: T }) {
  if (!canManage) return null;
  return (
    <Link to={WORDFENCE_SETTINGS_PATH} className="text-primary hover:underline">
      {t("monitoring.security.settingsLink")}
    </Link>
  );
}

/** The feed download's last error in words (the Wordfence settings' own), or the code as it came. */
function feedReason(error: string, t: T): string {
  const key = feedErrorKey(error);
  return key ? t(`settings.wordfence.feedErrors.${key}`) : error;
}

/** The line of a site whose package leaves the scan out; a site with no package is told it needs one. */
function notInPlanKey(hasPackage: boolean, kept: boolean): string {
  if (hasPackage) return kept ? "monitoring.security.notInPlanKept" : "monitoring.security.notInPlan";
  return kept ? "monitoring.security.noPackageKept" : "monitoring.security.noPackage";
}

export function SecurityCard({ monitorId, allowed, hasPackage, canManage, t }: SecurityCardProps) {
  const { data, failed, stillRunning, reload, runNow } = useSecurity(monitorId, allowed);
  const toast = useToast();
  const [starting, setStarting] = useState(false);

  async function start() {
    setStarting(true);
    try {
      await runNow();
    } catch (err) {
      toast(t(`monitoring.error.${monitorErrorKey(err)}`));
      // A refusal often means the card is behind (a scan somebody else started, a package or key changed).
      void reload();
    } finally {
      setStarting(false);
    }
  }

  const title = (
    <span className="inline-flex items-center gap-2">
      <ShieldCheck className="size-4 text-muted-foreground" strokeWidth={1.8} />
      {t("monitoring.security.title")}
    </span>
  );

  if (!data) {
    return (
      <section className="crm-card mb-4">
        <CardHead title={title} />
        <div className="flex justify-center px-[26px] pb-6 text-muted-foreground">
          {failed ? (
            <div className="text-center">
              <p className="mb-3 text-[13px] text-[var(--color-destructive)]">{t("monitoring.security.loadFailed")}</p>
              <Btn small onClick={() => void reload()}>
                {t("monitoring.retry")}
              </Btn>
            </div>
          ) : (
            <Loader2 className="size-5 animate-spin" />
          )}
        </div>
      </section>
    );
  }

  const state = securityCardState(data);
  const scan = data.latest;
  const sub = scan ? t("monitoring.security.lastScan", { when: fmtDateTime(scan.at) }) : t("monitoring.security.sub");
  const busy = starting || data.running;

  const actions = (
    <>
      {scan && !data.running ? <StatusPill tone={verdictTone(scan.verdict)}>{t(`monitoring.security.verdict.${scan.verdict}`)}</StatusPill> : null}
      {data.running ? <StatusPill tone="info">{t("monitoring.security.runningPill")}</StatusPill> : null}
      {canManage && canScanNow(data) ? (
        <Btn small disabled={busy} onClick={() => void start()}>
          {busy ? <Loader2 className="size-[15px] animate-spin" /> : <RefreshCw className="size-[15px]" strokeWidth={1.8} />}
          {t("monitoring.security.run")}
        </Btn>
      ) : null}
    </>
  );

  const line = "crm-sub px-[26px] pb-4 text-[13px]";
  return (
    <section className="crm-card mb-4">
      <CardHead title={title} sub={sub} actions={actions} />

      {state === "notInPlan" ? <p className={line}>{t(notInPlanKey(hasPackage, scan !== null))}</p> : null}
      {state === "noKey" ? (
        <p className={line}>
          {t("monitoring.security.noKey")} {canManage ? <SettingsLink canManage t={t} /> : t("monitoring.security.noKeyAsk")}
        </p>
      ) : null}
      {state === "noFeed" ? (
        <p className={line}>
          {data.feed.error
            ? t("monitoring.security.noFeedFailed", { reason: feedReason(data.feed.error, t) })
            : t("monitoring.security.noFeed")}{" "}
          {canManage ? <SettingsLink canManage t={t} /> : data.feed.error ? t("monitoring.security.fixAsk") : null}
        </p>
      ) : null}
      {state === "scanned" && data.feed.keySet && data.feed.stale ? (
        <p className={line}>
          {data.feed.fetchedAt
            ? t("monitoring.security.feedStale", { when: fmtDateTime(data.feed.fetchedAt) })
            : t("monitoring.security.feedMissing")}{" "}
          {data.feed.error ? `${t("monitoring.security.feedFailed", { reason: feedReason(data.feed.error, t) })} ` : null}
          {canManage ? <SettingsLink canManage t={t} /> : data.feed.error ? t("monitoring.security.fixAsk") : null}
        </p>
      ) : null}
      {state === "never" && !data.running ? (
        <p className={line}>
          {data.nextDueAt ? t("monitoring.security.neverDue", { when: fmtDateTime(data.nextDueAt) }) : t("monitoring.security.never")}
        </p>
      ) : null}

      {data.running && stillRunning ? (
        // Polling has stopped: no spinner, and a way to look again.
        <div className="flex flex-wrap items-center gap-3 px-[26px] pb-4">
          <p className="crm-sub text-[13px]">{t("monitoring.security.stillRunning")}</p>
          <Btn small onClick={() => void reload()}>
            <RefreshCw className="size-[15px]" strokeWidth={1.8} />
            {t("monitoring.security.refresh")}
          </Btn>
        </div>
      ) : null}
      {data.running && !stillRunning ? (
        <p className={`${line} flex items-center gap-2`}>
          <Loader2 className="size-4 shrink-0 animate-spin" />
          {t("monitoring.security.runningBody")}
        </p>
      ) : null}
      {data.lastError && !data.running ? (
        <p className="px-[26px] pb-4 text-[13px] text-[var(--color-destructive)]">
          {t("monitoring.security.lastError", {
            when: fmtDateTime(data.lastError.at),
            reason: t(`monitoring.security.errors.${data.lastError.error === "timeout" ? "timeout" : "failed"}`),
          })}
        </p>
      ) : null}

      {scan ? <ScanDetails scan={scan} history={data.history} t={t} /> : null}
      {scan && state === "scanned" && data.nextDueAt && !data.running ? (
        <p className="crm-mut px-[26px] pb-4 text-[12px]">{t("monitoring.security.nextDue", { when: fmtDateTime(data.nextDueAt) })}</p>
      ) : null}
      {scan ? <Attributions attributions={scan.attributions} t={t} /> : null}
    </section>
  );
}
